import React, {
  ReactElement,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import DropdownList from './DropdownList';
import { CollectionImage } from 'logic/images';
import { Attributes } from '../logic/NFT';
import { Media } from './Media';
import Link from 'next/link';
import { routes } from '@utils/routes';

function CollectionImageItem({
  img,
  onReceiveMetadata,
  onMint,
}: {
  img: CollectionImage;
  onReceiveMetadata: (
    meta: {
      attributes: Attributes;
      name: string;
      image: string;
    },
    image: CollectionImage
  ) => unknown;
  onMint?: (projectId: any) => Promise<void>;
}): ReactElement {
  const [hasError, setHasError] = useState(false);
  /**
   * For each image, we have to request metadata from arweave
   * To prevent blocking UI on many requests, let each image individually request
   */
  useEffect(() => {
    const hasMetadata = img.imageName && img.imagePath;
    if (hasMetadata) return;
    (async () => {
      const { name, image, attributes } = (await img.getMetadata?.()) ?? {};
      const imageName = img.cid ? name : 'PLACEHOLDER FOR NAME';
      const filteredAttributes = attributes.filter(
        (v) =>
          /**
           * Filter out attribute fields that are already displayed on the page
           */
          !['Collection', 'Medium', 'Commission Tier', 'Season'].includes(
            v.trait_type
          )
      );
      onReceiveMetadata(
        {
          name: imageName,
          image,
          attributes: filteredAttributes,
        },
        img
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img.id, img.cid, onReceiveMetadata]);


  return (
    <div className=" my-4 sm:my-0" key={`${img.id}-${img.tokenId}`}>
      {img.imagePath && (
        <Link href={routes.collectionGallery(img.slug, img.tokenId)}>
          <a className="relative my-auto sm:row-span-4 cursor-pointer">
            <Media
              height="100%"
              width="100%"
              layout="responsive"
              placeholder="blur"
              objectFit="contain"
              className="w-full  object-bottom"
              alt=""
              onError={() => {
                setHasError(true);
              }}
              quality={60}
              src={
                hasError || img.type === 'video'
                  ? img.metadataImagePath
                  : img.imagePath
              }
            />
          </a>
        </Link>
      )}
      <div className="sm:row-span-1">
        <p className="font-bold mt-1">
          {img.imageName || 'PLACEHOLDER FOR NAME'}
        </p>

        {onMint && img.claimedStatus === 'unclaimed' && (
          <p className="text-sm mt-4">
            <div className="my-4 mt-9 hover:opacity-70 cursor-pointer">
              <a
                onClick={() => onMint(img)}
                className="font-medium text-xl border-solid border-2 border-black px-7 py-3"
              >
                MINT
              </a>
            </div>
          </p>
        )}
        {onMint && img.claimedStatus === 'claimed' && (
          <p className="text-sm mt-4">
            <div className="my-4 mt-9 hover:opacity-70 cursor-not-allowed">
              <a className="opacity-50 font-medium text-xl border-solid border-2 border-black px-7 py-3">
                CLAIMED
              </a>
            </div>
          </p>
        )}
      </div>
    </div>
  );
}

export default function CollectionImages({
  images,
  onMint,
}: {
  images: CollectionImage[];
  onMint?: (projectId: any) => Promise<void>;
}): ReactElement {
  const [claimedStatus, setClaimedStatus] = useState<null | string>(null);
  const [photographer, setPhotographer] = useState<null | string>(null);
  const [imagesWithMetadata, setImagesWithMetadata] =
    useState<CollectionImage[]>(images);

  useEffect(() => {
    setImagesWithMetadata(images);
  }, [images]);
  const allPhotographers = useMemo(
    () =>
      images &&
      images.map((img) => img.name).filter((x, i, a) => a.indexOf(x) == i),
    [images]
  );


  const collectionImages = useMemo(
    () =>
      imagesWithMetadata &&
      imagesWithMetadata
        .filter((d) =>
          photographer ? d.name.toLowerCase() === photographer.toLowerCase() : d
        )
        .filter((d) => (claimedStatus ? d.claimedStatus === claimedStatus : d)),
    [imagesWithMetadata, photographer, claimedStatus]
  );

  useEffect(() => {
    console.log('fcaepockfepocfce', collectionImages)
  }, [])

  const onReceiveMetadata = useCallback(
    (metadata, img) => {
      const idx = images.findIndex((i) => i.tokenId === img.tokenId);
      setImagesWithMetadata((imgs) => [
        ...imgs.slice(0, idx),
        {
          ...img,
          imageName: metadata.name,
          imagePath: imgs[idx].imagePath ? imgs[idx].imagePath : metadata.image,
          imageAttributes: metadata.attributes,
        },
        ...imgs.slice(idx + 1),
      ]);
    },
    [images]
  );

  return (
    <div>
      <div className="mx-4 text-black font-bold sm:inline-flex">
        {allPhotographers.length > 1 && (
          <span className="mr-8">Filter By:</span>
        )}
        {allPhotographers && allPhotographers?.length > 1 && (
          <DropdownList
            title="photographer"
            options={[{ value: null, label: 'ALL' }, ...allPhotographers]}
            selected={photographer}
            setSelected={setPhotographer}
          />
        )}
        {/* Only display claim filter if we can mint by image */}
        {onMint && (
          <DropdownList
            title="claimed status"
            options={['claimed', 'unclaimed']}
            selected={claimedStatus}
            setSelected={setClaimedStatus}
          />
        )}
      </div>
      <div className="grid sm:grid-col-1 md:grid-cols-2 lg:grid-cols-3 sm:gap-x-8 sm:gap-y-4 text-black m-4">
        {collectionImages &&
          collectionImages.map((img: CollectionImage, idx) => (
            <CollectionImageItem
              key={idx}
              img={img}
              onReceiveMetadata={onReceiveMetadata}
              onMint={onMint}
            />
          ))}
      </div>
    </div>
  );
}
